<!--@format-->
<template>
    <div :class="['company-edit-widget']">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="currentStep.title === 'Edit Company'" class="step">
                    <div class="bordered-container">
                        <div class="title">Company Information</div>
                        <div class="company-form-group">
                            <div class="company-profile-image">
                                <image-upload
                                    id="company-image-upload"
                                    :uploadUrl="uploadUrl"
                                    :placeholderImage="placeholderImage"
                                    :providedImageUrl="company.logoUrl"
                                    orientation="horizontal"
                                    @fileUploadSuccess="updateImageUrl"
                                />
                            </div>
                            <div class="input-container">
                                <label>Type</label>
                                <company-types-dropdown
                                    v-model="company.companyType"
                                    :clientId="company.clientId"
                                />
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.name" label="Company Name" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.website" label="Website" />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.email" label="Email" />
                                </div>
                            </div>
                            <div class="input-container">
                                <label>Assigned Users</label>
                                <agency-users-search
                                    :options="getUserOptions()"
                                    :selected="company.assignedUsers"
                                    @assign="assignUser($event)"
                                    @remove="removeUser($event)"
                                />
                            </div>
                            <hr />
                            <div class="input-container">
                                <label>Billing</label>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.address" label="Address" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.address2" label="Address 2" />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.city" label="City" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.region" label="State" />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.zipCode" label="Zip Code" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.country" label="Country" />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.phone" label="Phone" />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="company.supportPhone"
                                        label="Support Phone"
                                    />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.salesPhone" label="Sales Phone" />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="company.accountingPhone"
                                        label="Accounting Phone"
                                    />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.fax" label="Fax" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">About this Company</div>
                        <div class="company-form-group">
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="company.employeeCount"
                                        type="number"
                                        label="Employees"
                                    />
                                </div>
                                <div class="input-container">
                                    <app-input
                                        v-model="company.deviceCount"
                                        type="number"
                                        label="Number of Devices"
                                    />
                                </div>
                            </div>
                            <div class="input-container">
                                <label>Vertical</label>
                                <vertical-dropdown
                                    v-model="company.vertical"
                                    :clientId="company.clientId"
                                />
                            </div>
                            <div class="input-container description-container">
                                <label>Description</label>
                                <app-text-area v-model="company.description" :rows="5" />
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">Social Media</div>
                        <div class="company-form-group">
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input v-model="company.linkedinUrl" label="LinkedIn URL" />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.facebookUrl" label="Facebook URL" />
                                </div>
                            </div>
                            <div class="company-form-row">
                                <div class="input-container">
                                    <app-input
                                        v-model="company.twitterUrl"
                                        label="X (Formerly Twitter) URL"
                                    />
                                </div>
                                <div class="input-container">
                                    <app-input v-model="company.youtubeUrl" label="YouTube URL" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="customCompanyFields.length > 0" class="bordered-container">
                        <div class="title">Custom Fields</div>
                        <div class="company-form-group">
                            <div
                                v-for="element in customCompanyFields"
                                :key="element.id"
                                class="company-form-row"
                            >
                                <div class="input-container custom-field-container">
                                    <custom-field-design-element :element="element" />
                                </div>
                            </div>
                            <div>
                                <app-button class="add" @click="openCustomFieldMenu()">
                                    Add a Custom Field
                                    <o6-add-svg />
                                </app-button>
                                <p class="disclaimer">
                                    To manage your custom fields, go to your
                                    <router-link
                                        @click.native="closeWidget"
                                        :to="`${routeBaseUrl}/Account/Customization/CustomCompanyFields`"
                                        >account settings</router-link
                                    >.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="bordered-container">
                        <div class="title">Locations</div>
                        <company-locations
                            :items="company.locations"
                            @remove-location="removeLocation"
                            @select-hq="updateHq"
                        />
                        <app-button class="add" @click="addLocation">
                            Add Location
                            <o6-add-svg />
                        </app-button>
                    </div>

                    <div class="bordered-container">
                        <div class="title">Account Details</div>
                        <div class="company-form-group">
                            <div class="input-container">
                                <div class="switch-container">
                                    <span>Mark as Account</span>
                                    <app-switch
                                        :value="company.isAccount"
                                        size="large"
                                        switch-text="YES"
                                        @input="toggleAccount"
                                    />
                                </div>
                                <span class="description-text">
                                    By marking this company as an account you'll be able to filter
                                    through companies that are currently under contract and assign
                                    users to them.
                                </span>
                            </div>
                            <div v-if="company.isAccount" class="input-container">
                                <app-date-picker
                                    v-model="company.accountStartDate"
                                    label="Account start date"
                                    format="MMM dd, yyyy"
                                    @click.native.stop
                                />
                            </div>
                            <div v-if="company.isAccount" class="input-container">
                                <app-date-picker
                                    v-model="company.contractStartDate"
                                    label="Contract start date"
                                    format="MMM dd, yyyy"
                                    @click.native.stop
                                />
                            </div>
                            <div v-if="company.isAccount" class="input-container">
                                <app-date-picker
                                    v-model="company.contractEndDate"
                                    label="Contract end date"
                                    format="MMM dd, yyyy"
                                    @click.native.stop
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="(currentStep.title = 'Loading')" class="step">
                    <loading-screen-svg />
                </div>
                <div v-else-if="currentStep.title === 'Saving'" class="step scrubbing-step">
                    <quick-add-company-svg />
                </div>
            </div>

            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" :onClick="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button :loading="currentStep.loading" :onClick="currentStep.action">
                        {{ currentStep.buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AgencyUsersSearch = () => import('@/components/client/agency-users-search.vue');
const AppButton = () => import('@/components/common/app-button.vue');
const AppDatePicker = () => import('@/components/common/app-date-picker');
const AppInput = () => import('@/components/common/app-input.vue');
const AppTextArea = () => import('@/components/common/app-textarea');
const AppSwitch = () => import('@/components/common/app-switch');
const CompanyLocations = () => import('@/components/company/company-locations.vue');
const CompanyTypesDropdown = () => import('@/components/company/company-types-dropdown');
const CustomFieldDesignElement = () =>
    import('@/components/custom-field/custom-field-design-element');
const ImageUpload = () => import('@/components/common/image-upload.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const Multiselect = () => import('vue-multiselect');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const QuickAddScrubbingSvg = () => import('@/components/svg/quick-add/quick-add-scrubbing-svg.vue');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const ServiceDropdown = () => import('@/components/service/service-dropdown');
const VerticalDropdown = () => import('@/components/vertical/vertical-dropdown');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import { CustomFieldsServiceMixin } from '@/mixins/custom-fields-service-mixin';
import { differenceBy, omit } from 'lodash';
import EventBus from '@/scripts/event-bus.js';
import log from '@/scripts/logger';

export default {
    name: 'company-edit-widget',

    components: {
        'agency-users-search': AgencyUsersSearch,
        'app-button': AppButton,
        'app-date-picker': AppDatePicker,
        'app-input': AppInput,
        'app-text-area': AppTextArea,
        'app-switch': AppSwitch,
        'company-locations': CompanyLocations,
        'company-types-dropdown': CompanyTypesDropdown,
        'custom-field-design-element': CustomFieldDesignElement,
        'image-upload': ImageUpload,
        'loading-screen-svg': LoadingScreenSvg,
        multiselect: Multiselect,
        'o6-add-svg': O6AddSvg,
        'quick-add-company-svg': QuickAddScrubbingSvg,
        's3-close-svg': S3CloseSvg,
        'service-dropdown': ServiceDropdown,
        'vertical-dropdown': VerticalDropdown,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [CustomFieldsServiceMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            placeholderImage: require('@/assets/images/company-avatar.png'),
            uploadUrl: '/api/Files',
            company: {
                id: null,
                isAccount: null,
                accountStartDate: null,
                contractStartDate: null,
                contractEndDate: null,
                clientId: null,
                email: null,
                name: null,
                phone: null,
                salesPhone: null,
                supportPhone: null,
                accountingPhone: null,
                fax: null,
                address: null,
                address2: null,
                zipCode: null,
                region: null,
                logoUrl: null,
                website: null,
                domain: null,
                linkedinUrl: null,
                facebookUrl: null,
                twitterUrl: null,
                youtubeUrl: null,
                city: null,
                country: null,
                employeeCount: null,
                deviceCount: null,
                description: null,
                companyType: null,
                vertical: null,
                locations: [],
                assignedUsers: [],
            },

            currentStep: null,

            steps: {
                company: {
                    title: 'Edit Company',
                    buttonText: 'Save',
                    loading: false,
                    action: this.saveChanges,
                },

                saving: { title: 'Saving...', buttonText: 'Saving', loading: true },
                loading: { title: 'Loading...', buttonText: 'Loading...', loading: true },
            },

            availableUsers: [],
            customCompanyFields: [],
        };
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        routeBaseUrl() {
            return this.$store.getters.routeBaseUrl;
        },

        customFieldsConfig() {
            return { type: 'Company', clientId: this.clientId, companyId: this.company.id };
        },
    },

    created() {
        this.company.id = this.widget.options.companyId;
        this.goToStep('loading');

        this.getCompany().then(() => {
            this.getClientUsers().then(() => {
                this.getCustomCompanyFields()
                    .then(() => {
                        this.goToStep('company');
                    })
                    .catch((error) => {
                        log.error('Failed to get data', error);
                        this.closeWidget();
                    });
            });
        });

        EventBus.$on('custom-field:update', this.getCustomCompanyFields);
    },

    beforeDestroy() {
        EventBus.$off('custom-field:update', this.getCustomCompanyFields);
    },

    methods: {
        addLocation() {
            const newLocation = {
                id: this.$helpers.createId('temp-Id'),
                name: null,
                address: null,
                seconAddress: null,
                city: null,
                region: null,
                zipCode: null,
                country: null,
                phone: null,
                supportPhone: null,
                salesPhone: null,
                accountingPhone: null,
                fax: null,
                isHq: false,
            };

            this.company.locations.push(newLocation);
        },

        removeLocation(removeId) {
            this.company.locations = this.company.locations.filter(({ id }) => id !== removeId);
        },

        updateHq(hqId) {
            this.company.locations.forEach((location) => {
                if (location.id === hqId) {
                    location.isHq = true;
                } else {
                    location.isHq = false;
                }
            });
        },

        assignUser(userToAssign) {
            this.company.assignedUsers.push(userToAssign);
        },

        removeUser(userToRemove) {
            this.company.assignedUsers = this.company.assignedUsers.filter(
                ({ id }) => id !== userToRemove.id,
            );
        },

        toggleAccount(isAccount) {
            this.company.isAccount = isAccount;
            if (isAccount) {
                const accountDate = new Date();
                this.company.accountStartDate = accountDate;
            } else {
                this.company.accountStartDate = null;
                this.company.contractStartDate = null;
                this.company.contractEndDate = null;
            }
        },

        getCompany() {
            return new Promise((resolve, reject) => {
                this.$api
                    .get(
                        `/service/Companies?$filter=Id eq ${this.company.id}&$expand=CompanyType,Vertical,CompanyLocations,CompanyUsers($expand=User)`,
                    )
                    .then((response) => {
                        this.company.clientId = response.data.value[0].ClientId;
                        this.company.logoUrl = response.data.value[0].LogoUrl;
                        this.company.linkedinUrl = response.data.value[0].LinkedinUrl;
                        this.company.facebookUrl = response.data.value[0].FacebookUrl;
                        this.company.twitterUrl = response.data.value[0].TwitterUrl;
                        this.company.youtubeUrl = response.data.value[0].YoutubeUrl;
                        this.company.name = response.data.value[0].Name;
                        this.company.description = response.data.value[0].Description || '';
                        this.company.address = response.data.value[0].Address;
                        this.company.address2 = response.data.value[0].Address2;
                        this.company.zipCode = response.data.value[0].ZipCode;
                        this.company.city = response.data.value[0].City;
                        this.company.region = response.data.value[0].Region;
                        this.company.country = response.data.value[0].Country;
                        this.company.website = response.data.value[0].Website;
                        this.company.email = response.data.value[0].Email;
                        this.company.phone = response.data.value[0].Phone;
                        this.company.salesPhone = response.data.value[0].SalesPhone;
                        this.company.supportPhone = response.data.value[0].SupportPhone;
                        this.company.accountingPhone = response.data.value[0].AccountingPhone;
                        this.company.fax = response.data.value[0].Fax;
                        this.company.employeeCount =
                            response.data.value[0].ApproximateEmployeeCount;
                        this.company.deviceCount = response.data.value[0].DeviceCount;
                        this.company.isAccount = response.data.value[0].IsAccount;
                        this.company.accountStartDate = response.data.value[0].AccountStartDate
                            ? new Date(response.data.value[0].AccountStartDate)
                            : null;
                        this.company.contractStartDate = response.data.value[0].ContractStartDate
                            ? new Date(response.data.value[0].ContractStartDate)
                            : null;
                        this.company.contractEndDate = response.data.value[0].ContractEndDate
                            ? new Date(response.data.value[0].ContractEndDate)
                            : null;
                        this.company.assignedUsers = response.data.value[0].CompanyUsers.map(
                            ({ User }) => this.mapClientUserToUserOption(User),
                        );

                        if (response.data.value[0].Vertical) {
                            this.company.vertical = {
                                id: response.data.value[0].Vertical.Id,
                                name: response.data.value[0].Vertical.Name,
                            };
                        }

                        if (response.data.value[0].CompanyType) {
                            this.company.companyType = {
                                id: response.data.value[0].CompanyType.Id,
                                name: response.data.value[0].CompanyType.Name,
                                color: response.data.value[0].CompanyType.Color,
                                backgroundClass: this.$helpers.getCompanyTypeBackgroundClass(
                                    response.data.value[0].CompanyType.Color,
                                ),
                            };
                        }

                        this.company.locations = response.data.value[0].CompanyLocations.map(
                            (location) => ({
                                id: location.Id,
                                name: location.Name,
                                address: location.Address,
                                secondAddress: location.SecondAddress,
                                city: location.City,
                                region: location.Region,
                                zipCode: location.ZipCode,
                                country: location.Country,
                                phone: location.Phone,
                                supportPhone: location.SupportPhone,
                                salesPhone: location.SalesPhone,
                                accountingPhone: location.AccountingPhone,
                                fax: location.Fax,
                                isHq: location.IsHQ,
                            }),
                        );

                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your company data', {
                            error,
                        });
                        reject();
                    });
            });
        },

        getClientUsers() {
            return new Promise((resolve, reject) => {
                const filter = `$filter=ClientId eq ${this.clientId} and IsActive eq true`;
                const query = `/service/Users?${filter}`;
                this.$api
                    .get(query)
                    .then((response) => {
                        this.availableUsers = response.data.value.map(
                            this.mapClientUserToUserOption,
                        );
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching users.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        mapClientUserToUserOption(user) {
            return {
                id: user.Id,
                name: `${user.FirstName} ${user.LastName}`,
                profilePicture: user.ProfileImageUrl,
                title: user.Title,
            };
        },

        getUserOptions() {
            return differenceBy(this.availableUsers, this.company.assignedUsers, 'id');
        },

        saveChanges() {
            this.saveCompany()
                .then(this.saveCustomFields)
                .then(() => {
                    this.$helpers.showSuccessNotification('Your changes have been saved.');
                    EventBus.$emit('company:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    log.error('Failed to save company', error);
                    this.goToCompanyStep();
                });
        },

        saveCompany() {
            this.currentStep = this.steps['saving'];

            var changes = [
                { name: 'LogoUrl', value: this.company.logoUrl },
                { name: 'Name', value: this.company.name },
                { name: 'Email', value: this.company.email },
                { name: 'Phone', value: this.company.phone },
                { name: 'SalesPhone', value: this.company.salesPhone },
                { name: 'SupportPhone', value: this.company.supportPhone },
                { name: 'AccountingPhone', value: this.company.accountingPhone },
                { name: 'Fax', value: this.company.fax },
                { name: 'Address', value: this.company.address },
                { name: 'Address2', value: this.company.address2 },
                { name: 'ZipCode', value: this.company.zipCode },
                { name: 'Region', value: this.company.region },
                { name: 'LinkedinUrl', value: this.company.linkedinUrl },
                { name: 'FacebookUrl', value: this.company.facebookUrl },
                { name: 'TwitterUrl', value: this.company.twitterUrl },
                { name: 'YoutubeUrl', value: this.company.youtubeUrl },
                { name: 'Description', value: this.company.description },
                { name: 'Website', value: this.company.website },
                { name: 'City', value: this.company.city },
                { name: 'Country', value: this.company.country },
                { name: 'ApproximateEmployeeCount', value: this.company.employeeCount },
                { name: 'DeviceCount', value: this.company.deviceCount },
                { name: 'IsAccount', value: this.company.isAccount },
                {
                    name: 'AccountStartDate',
                    value: this.company.accountStartDate
                        ? this.company.accountStartDate.toISOString()
                        : null,
                },
                {
                    name: 'ContractStartDate',
                    value: this.company.contractStartDate
                        ? this.company.contractStartDate.toISOString()
                        : null,
                },
                {
                    name: 'ContractEndDate',
                    value: this.company.contractEndDate
                        ? this.company.contractEndDate.toISOString()
                        : null,
                },
                {
                    name: 'Users',
                    value: this.company.assignedUsers.map(({ id }) => ({ UserId: id })),
                },
                {
                    name: 'CompanyTypeId',
                    value: this.company.companyType ? this.company.companyType.id : null,
                },
                {
                    name: 'VerticalId',
                    value: this.company.vertical ? this.company.vertical.id : null,
                },
                {
                    name: 'Locations',
                    value: this.company.locations.map((location) => omit(location, 'id')),
                },
            ];

            return new Promise((resolve, reject) => {
                this.$api
                    .patch(`/api/Companies/${this.company.id}`, changes)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to save your company', {
                            error,
                        });
                        reject();
                    });
            });
        },

        getCustomCompanyFields(options) {
            return new Promise((resolve, reject) => {
                this.getCustomFields(options, this.customFieldsConfig)
                    .then(({ items }) => {
                        return this.getCustomFieldValues(items);
                    })
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your custom fields data.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getCustomFieldValues(items) {
            return new Promise((resolve, reject) => {
                const valuesRequests = items.map((item) => {
                    return this.getCustomFieldValue(this.customFieldsConfig, item);
                });

                Promise.all(valuesRequests)
                    .then((values) => {
                        this.customCompanyFields = values;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        saveCustomFields() {
            const service = '/ms/custom-fields/v1/customCompanyFieldValues';
            return new Promise((resolve, reject) => {
                const valueRequests = this.customCompanyFields.map((item) => {
                    const customFieldValue = {
                        CompanyId: this.company.id,
                        CustomCompanyFieldId: item.id,
                        [item.customFieldValue.name]: this.customFieldValue(
                            item.customFieldValue.value,
                            item.inputType,
                        ),
                    };

                    if (item.customFieldValueId) {
                        return this.$api.put(
                            `${service}/${item.customFieldValueId}/${this.clientId}`,
                            customFieldValue,
                        );
                    }

                    return this.$api.post(`${service}/${this.clientId}`, customFieldValue);
                });

                Promise.all(valueRequests)
                    .then(resolve)
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'Failed to save your company custom fields',
                            { error },
                        );
                        reject();
                    });
            });
        },

        customFieldValue(value, type) {
            if (type.includes('checkbox') || type.includes('dropdown')) {
                return JSON.stringify(value);
            } else if (type === 'number') {
                return +value;
            }
            return value;
        },

        updateImageUrl(imageUrl) {
            this.company.logoUrl = imageUrl;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        goToCompanyStep() {
            this.currentStep = this.steps['company'];
        },

        goToCompany() {
            this.closeWidget();
            this.$router.push(`/Companies/${this.company.id}`);
        },

        openCustomFieldMenu() {
            EventBus.$emit('open-widget', {
                name: 'custom-field-widget',
                options: {
                    type: 'Company',
                    clientId: this.company.clientId,
                },
            });
            this.saveCustomFields();
        },
    },
};
</script>

<style scoped>
.company-edit-widget .step,
.company-edit-widget .bordered-container .add {
    width: 100%;
}

.company-edit-widget .step .loading-screen {
    margin: 0 auto;
    text-align: center;
}

.company-edit-widget .step .company-form-row {
    display: flex;
    justify-content: space-between;
}

.company-edit-widget .step .bordered-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    margin-bottom: 2.5em;
    padding: 32px;
    position: relative;
}

.company-edit-widget .step .bordered-container .company-form-group {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.company-edit-widget .step .bordered-container .title {
    background-color: #ffffff;
    font-weight: 600;
    left: 50%;
    margin-left: -82px;
    padding: 0px 25px;
    position: absolute;
    top: -11px;
}

.company-edit-widget .step .bordered-container hr {
    border-top: 2px solid #e9e9ef;
    margin: 0;
}

.company-edit-widget .step .company-form-row .input-container {
    display: flex;
    flex-direction: column;
    width: 48%;
}

.company-edit-widget .step .company-form-group .input-container label {
    font-weight: 500;
}

.company-edit-widget .step .company-form-group .input-container .description-text {
    font-size: 14px;
}

.company-edit-widget .step .company-form-group .input-container .switch-container {
    align-items: center;
    display: flex;
    font-weight: 500;
    margin-bottom: 8px;
}

.company-edit-widget .step .company-form-group .switch-container .app-switch {
    width: unset;
    margin: 0 16px;
}

.company-edit-widget .step .company-form-row .description-container,
.company-edit-widget .step .company-form-row .custom-field-container {
    width: 100%;
}

.company-edit-widget .step .input-container textarea {
    border: 1px solid #e9e9f0;
    max-width: 100%;
    min-height: 150px;
    width: 100%;
}

.company-edit-widget .step .service-row {
    margin-bottom: 24px;
}

.company-edit-widget .action-container {
    display: flex;
}

.company-edit-widget .action-container .btn {
    margin-left: 15px;
}

.company-edit-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.company-edit-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.company-edit-widget .action-container .btn.transparent-btn {
    background: transparent;
    color: #18aeff;
}

.company-edit-widget .action-container .btn.transparent-btn:hover {
    background: #dbedfa;
}

.company-edit-widget .bordered-container .add svg {
    margin-left: 16px;
}
</style>

<style>
.company-edit-widget .bordered-container .add svg path {
    fill: #ffffff;
}

.company-edit-widget .disclaimer {
    font-size: 12px;
    padding: 4px 0px;
}

.company-edit-widget .disclaimer a {
    color: #18aeff;
}
</style>
